import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

// ─── Review Status Badge (mirrors LeadContacts' EligibilityBadge pattern) ──────

function ReviewStatusBadge({ status }) {
  const config = {
    pending: { label: 'PENDING', bg: '#d9770622', color: '#f97316', border: '#f97316' },
    cleared: { label: 'CLEARED', bg: '#16a34a22', color: '#22c55e', border: '#22c55e' },
    flagged: { label: 'FLAGGED', bg: '#dc262622', color: '#ef4444', border: '#ef4444' },
  };
  const cfg = config[status] || config.pending;
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function CompCard({ txn, onReview, updating }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(txn.note || '');

  return (
    <div style={{
      background: 'var(--surface-2, #111)',
      border: `1px solid ${txn.reviewStatus === 'flagged' ? '#ef444440' : 'var(--border, #222)'}`,
      borderRadius: 8, marginBottom: 8, padding: '10px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text, #e5e5e5)', fontFamily: 'DM Sans, sans-serif' }}>
              {txn.name}
            </span>
            <span style={{
              background: '#ffffff11', color: '#aaa', border: '1px solid #333',
              borderRadius: 4, padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
            }}>
              {txn.category}
            </span>
            <ReviewStatusBadge status={txn.reviewStatus} />
          </div>
          <div style={{ fontSize: 11, color: '#777', marginBottom: 2 }}>
            {txn.email || txn.memberId} · {new Date(txn.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {txn.description}
          </div>
          {txn.reviewedBy && (
            <div style={{ fontSize: 10, color: '#666', marginTop: 4, fontStyle: 'italic' }}>
              Reviewed {new Date(txn.reviewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            disabled={updating}
            onClick={() => onReview(txn.id, txn.locationId, 'cleared', note)}
            style={{
              background: txn.reviewStatus === 'cleared' ? '#22c55e' : 'var(--surface-3, #1a1a1a)',
              color: txn.reviewStatus === 'cleared' ? '#000' : '#22c55e',
              border: '1px solid #22c55e', borderRadius: 6,
              padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Clear
          </button>
          <button
            disabled={updating}
            onClick={() => onReview(txn.id, txn.locationId, 'flagged', note)}
            style={{
              background: txn.reviewStatus === 'flagged' ? '#ef4444' : 'var(--surface-3, #1a1a1a)',
              color: txn.reviewStatus === 'flagged' ? '#000' : '#ef4444',
              border: '1px solid #ef4444', borderRadius: 6,
              padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Flag
          </button>
          <button
            onClick={() => setNoteOpen(o => !o)}
            style={{
              background: 'var(--surface-3, #1a1a1a)', color: 'var(--text-muted)',
              border: '1px solid var(--border, #333)', borderRadius: 6,
              padding: '5px 10px', fontSize: 11, cursor: 'pointer',
            }}
          >
            {noteOpen ? '▲' : '📝'}
          </button>
        </div>
      </div>
      {noteOpen && (
        <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
          <input
            className="field-input"
            type="text"
            placeholder="Add a note..."
            value={note}
            onChange={e => setNote(e.target.value)}
            style={{ flex: 1, fontSize: 12 }}
          />
          <button
            disabled={updating}
            onClick={() => onReview(txn.id, txn.locationId, txn.reviewStatus, note)}
            style={{
              background: 'var(--yellow)', color: '#000', border: 'none',
              borderRadius: 6, padding: '5px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Save Note
          </button>
        </div>
      )}
      {txn.note && !noteOpen && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          "{txn.note}"
        </div>
      )}
    </div>
  );
}

export default function CompQueue() {
  const { get, post } = useApi();
  const { currentClub } = useClub();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('pending'); // pending | all
  const [updating, setUpdating] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    get(`/revenue/comp-queue?club=${currentClub.id}`)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentClub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleReview(transactionId, locationId, status, note) {
    setUpdating(prev => ({ ...prev, [transactionId]: true }));
    try {
      await post(`/revenue/comp-queue/${transactionId}/review`, { locationId, status, note });
      setData(prev => prev ? ({
        ...prev,
        transactions: prev.transactions.map(t =>
          t.id === transactionId ? { ...t, reviewStatus: status, note } : t
        ),
      }) : prev);
    } catch (e) {
      console.error('Comp review update failed', e);
    } finally {
      setUpdating(prev => ({ ...prev, [transactionId]: false }));
    }
  }

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (error) return (
    <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
      Failed to load Comp Queue — {error}
      <button onClick={load} style={{ marginLeft: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>
        Retry
      </button>
    </div>
  );

  const all = data?.transactions || [];
  const searchLower = search.toLowerCase();
  const filtered = all
    .filter(t => filter === 'all' || t.reviewStatus === 'pending')
    .filter(t =>
      !search ||
      t.name?.toLowerCase().includes(searchLower) ||
      t.description?.toLowerCase().includes(searchLower) ||
      t.category?.toLowerCase().includes(searchLower)
    );
  const pendingCount = all.filter(t => t.reviewStatus === 'pending').length;

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          💳 <strong style={{ color: 'var(--text)' }}>{pendingCount}</strong> pending review · {all.length} total $0 transactions
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['pending', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                background: filter === f ? 'var(--yellow)' : 'var(--surface-2)',
                color: filter === f ? '#000' : 'var(--text-muted)',
                border: '1px solid var(--border)', borderRadius: 6,
                padding: '4px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <input
        className="field-input"
        type="text"
        placeholder="Search by name, description, or category..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', marginBottom: 14 }}
      />

      {filtered.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 32, fontStyle: 'italic' }}>
          No {filter === 'pending' ? 'pending' : ''} $0 transactions to review.
        </div>
      ) : (
        filtered.map(txn => (
          <CompCard key={txn.id} txn={txn} onReview={handleReview} updating={updating[txn.id]} />
        ))
      )}
    </div>
  );
}
